// dNews / Email List
//
// Three tabs in one page:
//   1. Contacts - CRUD non-employee recipients with type/org/location.
//   2. Groups   - distribution lists composed of employees, contacts, raw emails.
//   3. Import   - CSV/XLSX bulk import with column mapping + dedup.
//
// Powers the "Contact" and "Group" target_types in Newsletter + Reminder.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import {
    FaUsers, FaUserPlus, FaFolderOpen, FaUpload, FaSearch, FaPlus, FaTrashAlt,
    FaPenSquare, FaTimes, FaPaperclip, FaArrowRight, FaDownload,
    FaEnvelopeOpenText, FaCheck, FaBan,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import DNewsSelect from "./utils/dNews_Select";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";
import "./dNews_EmailList.css";

const CONTACT_TYPES = ["client", "prospect", "media", "partner", "other"];

export default function DNewsEmailList({ navSize }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState("contacts");

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - Email List
                    </h2>
                    <p className="dspr-subtitle">Manage non-employee contacts, build distribution groups, and bulk-import from CSV/XLSX. Used as recipients in Newsletter + Reminder.</p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* ── Tab bar ──────────────────────────────────────── */}
                    <div className="dnews-el-tabs">
                        {[
                            { id: "contacts", label: "Contacts", icon: FaUsers },
                            { id: "groups",   label: "Groups",   icon: FaFolderOpen },
                            { id: "senders",  label: "Senders",  icon: FaEnvelopeOpenText },
                            { id: "import",   label: "Import",   icon: FaUpload },
                            { id: "unsubscribes", label: "Unsubscribes", icon: FaBan },
                        ].map(t => (
                            <button
                                key={t.id}
                                className={`dnews-el-tab ${tab === t.id ? "active" : ""}`}
                                onClick={() => setTab(t.id)}
                            >
                                <t.icon /> {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "contacts" && <ContactsTab navigate={navigate} />}
                    {tab === "groups"   && <GroupsTab   navigate={navigate} />}
                    {tab === "senders"  && <SendersTab  navigate={navigate} />}
                    {tab === "import"   && <ImportTab   navigate={navigate} />}
                    {tab === "unsubscribes" && <UnsubscribesTab />}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTACTS TAB
// ═══════════════════════════════════════════════════════════════════════════
function ContactsTab({ navigate }) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [typeFilter, setTypeFilter] = useState("");
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState(null);    // null | {} (new) | {existing}
    const [pasteOpen, setPasteOpen] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({ type: typeFilter, search });
        try {
            const res = await apiFetch(`/api/dnews/contacts/list?${params.toString()}`, { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            setContacts(await res.json());
        } finally { setLoading(false); }
    }, [typeFilter, search, navigate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const onDelete = async (c) => {
        const ok = await dnAlert.confirm({
            title: `Delete "${c.name}"?`,
            message: `${c.email} will be removed from contacts. Group memberships referencing this contact will also be removed.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await apiFetch(`/api/dnews/contacts/${c.id}`, { method: "DELETE", credentials: "include" });
        const data = await res.json();
        if (!res.ok) { dnAlert.error("Failed", data.error); return; }
        await fetchList();
    };

    return (
        <>
            <div className="dnews-el-toolbar">
                <div className="dnews-el-search">
                    <FaSearch />
                    <input
                        type="text"
                        placeholder="Search name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <DNewsSelect
                        value={typeFilter}
                        onChange={(v) => setTypeFilter(v)}
                        options={[
                            { value: "", label: "All types" },
                            ...CONTACT_TYPES.map(t => ({ value: t, label: t })),
                        ]}
                    />
                </div>
                <button className="dnews-el-secondary-btn" onClick={() => setPasteOpen(true)}>
                    <FaPaperclip /> Paste bulk
                </button>
                <button className="dnews-el-primary-btn" onClick={() => setModal({})}>
                    <FaPlus /> Add contact
                </button>
            </div>

            <div className="dnews-el-count">
                {loading ? "Loading..." : `${contacts.length} contact${contacts.length === 1 ? "" : "s"}`}
            </div>

            <div className="dnews-el-table-wrap">
                <table className="dnews-el-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Type</th>
                            <th>Org / Client</th>
                            <th>Location</th>
                            <th style={{ width: 90 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && contacts.length === 0 && (
                            <tr><td colSpan="6" className="dnews-el-empty">No contacts match. Try clearing filters or click <strong>+ Add contact</strong>.</td></tr>
                        )}
                        {contacts.map(c => (
                            <tr key={c.id}>
                                <td className="dnews-el-strong">{c.name}</td>
                                <td>{c.email}</td>
                                <td><span className={`dnews-el-type-pill ${c.type}`}>{c.type}</span></td>
                                <td>{c.org || "-"}</td>
                                <td>{c.location || "-"}</td>
                                <td>
                                    <button className="dnews-el-icon-btn" title="Edit" onClick={() => setModal(c)}>
                                        <FaPenSquare />
                                    </button>
                                    <button className="dnews-el-icon-btn danger" title="Delete" onClick={() => onDelete(c)}>
                                        <FaTrashAlt />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && <ContactModal initial={modal} onClose={() => setModal(null)} onSaved={fetchList} />}
            {pasteOpen && <PasteBulkModal onClose={() => setPasteOpen(false)} onSaved={fetchList} />}
        </>
    );
}

function ContactModal({ initial, onClose, onSaved }) {
    const isEdit = !!initial.id;
    const [name, setName]         = useState(initial.name || "");
    const [email, setEmail]       = useState(initial.email || "");
    const [type, setType]         = useState(initial.type || "client");
    const [org, setOrg]           = useState(initial.org || "");
    const [location, setLocation] = useState(initial.location || "");
    const [notes, setNotes]       = useState(initial.notes || "");
    const [busy, setBusy] = useState(false);

    const onSave = async () => {
        if (!name.trim())  { dnAlert.warning("Name is required");  return; }
        if (!email.trim()) { dnAlert.warning("Email is required"); return; }
        // Same regex as the backend's isValidEmail - catches "test @ gmail.com"
        // (spaces), missing TLD, missing @, etc. before the round-trip.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            dnAlert.warning("Invalid email", `"${email}" is not a valid email address.`);
            return;
        }
        setBusy(true);
        try {
            const url = isEdit ? `/api/dnews/contacts/${initial.id}` : "/api/dnews/contacts/create";
            const res = await apiFetch(url, {
                method: isEdit ? "PUT" : "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, type, org, location, notes }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            dnAlert.success(isEdit ? "Updated" : "Added");
            onSaved();
            onClose();
        } catch (e) {
            dnAlert.error("Save failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="dnews-el-modal-backdrop">
            <div className="dnews-el-modal" onClick={e => e.stopPropagation()}>
                <div className="dnews-el-modal-header">
                    <h3>{isEdit ? "Edit Contact" : "Add Contact"}</h3>
                    <button onClick={onClose}><FaTimes /></button>
                </div>
                <div className="dnews-el-modal-body">
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Name</label>
                        <input className="dnews-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Email</label>
                        <input className="dnews-input" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
                    </div>
                    <div className="dnews-el-modal-grid">
                        <div className="dnews-row">
                            <label className="dnews-label">Type</label>
                            <DNewsSelect
                                value={type}
                                onChange={(v) => setType(v)}
                                options={CONTACT_TYPES.map(t => ({ value: t, label: t }))}
                            />
                        </div>
                        <div className="dnews-row">
                            <label className="dnews-label">Org / Client</label>
                            <input className="dnews-input" value={org} onChange={(e) => setOrg(e.target.value)} maxLength={120} />
                        </div>
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label">Location</label>
                        <input className="dnews-input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={120} />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label">Notes (optional)</label>
                        <textarea className="dnews-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                </div>
                <div className="dnews-el-modal-footer">
                    <button className="dnews-el-secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="dnews-submit-btn" onClick={onSave} disabled={busy}>
                        {busy
                            ? <><span className="dn-spinner" /> Saving…</>
                            : (isEdit ? "Save changes" : "Add contact")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PasteBulkModal({ onClose, onSaved }) {
    const [text, setText] = useState("");
    const [type, setType] = useState("client");
    const [busy, setBusy] = useState(false);

    const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const tokens     = text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    const invalidArr = tokens.filter(e => !EMAIL_RX.test(e));
    const validArr   = tokens.filter(e =>  EMAIL_RX.test(e));

    const onSave = async () => {
        if (!text.trim()) { dnAlert.warning("Paste at least one email"); return; }
        // Strict all-or-nothing: any invalid email blocks the whole save.
        if (invalidArr.length > 0) {
            dnAlert.error(
                `${invalidArr.length} invalid email${invalidArr.length === 1 ? "" : "s"}`,
                `Fix or remove: ${invalidArr.slice(0, 3).join(", ")}${invalidArr.length > 3 ? "…" : ""}`
            );
            return;
        }
        setBusy(true);
        try {
            const res = await apiFetch("/api/dnews/contacts/paste", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text, type }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            // invalid was blocked above, so server-side skips are duplicates only.
            const dupMsg = data.skipped_dup > 0 ? `${data.skipped_dup} duplicates skipped` : undefined;
            if (data.added > 0)             dnAlert.success(`Added ${data.added}`, dupMsg);
            else if (data.skipped_dup > 0)  dnAlert.info("Nothing added", dupMsg);
            else                             dnAlert.warning("Nothing to add");
            onSaved(); onClose();
        } catch (e) {
            dnAlert.error("Failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="dnews-el-modal-backdrop">
            <div className="dnews-el-modal" onClick={e => e.stopPropagation()}>
                <div className="dnews-el-modal-header">
                    <h3>Paste bulk emails</h3>
                    <button onClick={onClose}><FaTimes /></button>
                </div>
                <div className="dnews-el-modal-body">
                    <div className="dnews-row">
                        <label className="dnews-label">Default type for all pasted contacts</label>
                        <DNewsSelect
                            value={type}
                            onChange={(v) => setType(v)}
                            options={CONTACT_TYPES.map(t => ({ value: t, label: t }))}
                        />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label">Emails (comma, space, or newline separated)</label>
                        <textarea
                            className={`dnews-input ${invalidArr.length > 0 ? "dnews-input-error" : ""}`}
                            rows={8}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="alice@acme.com, bob@example.com&#10;charlie@partner.io"
                        />
                        {tokens.length > 0 && (
                            invalidArr.length > 0 ? (
                                <div className="dnews-input-err-list">
                                    <strong>{invalidArr.length} invalid email{invalidArr.length === 1 ? "" : "s"}:</strong>
                                    {" "}
                                    {invalidArr.slice(0, 5).map((e, i) => (
                                        <span key={i} className="dnews-input-err-chip">{e}</span>
                                    ))}
                                    {invalidArr.length > 5 && <span> +{invalidArr.length - 5} more</span>}
                                </div>
                            ) : (
                                <div className="dnews-input-ok-hint">
                                    ✓ {validArr.length} valid email{validArr.length === 1 ? "" : "s"} ready
                                </div>
                            )
                        )}
                    </div>
                </div>
                <div className="dnews-el-modal-footer">
                    <button className="dnews-el-secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button
                        className="dnews-submit-btn"
                        onClick={onSave}
                        disabled={busy || invalidArr.length > 0}
                    >
                        {busy ? <><span className="dn-spinner" /> Adding…</> : "Add all"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// SENDERS TAB
//
// Manages the From-address whitelist for Newsletter + Reminder. Only
// addresses on a SendGrid-authenticated domain (dolluzcorp.com /
// dolluzcorp.in) can be added - the backend enforces this and the modal
// shows the rule inline so admins don't try arbitrary domains.
// ═══════════════════════════════════════════════════════════════════════════
const ALLOWED_SENDER_DOMAINS = ["dolluzcorp.com", "dolluzcorp.in"];

function SendersTab({ navigate }) {
    const [senders, setSenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dnews/senders/list", { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            setSenders(await res.json());
        } finally { setLoading(false); }
    }, [navigate]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const onToggle = async (s) => {
        try {
            const res = await apiFetch(`/api/dnews/senders/${s.id}/toggle`, {
                method: "PATCH", credentials: "include",
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || "Toggle failed");
            }
            fetchAll();
        } catch (e) { dnAlert.error("Failed", e.message); }
    };

    const onDelete = async (s) => {
        const ok = await dnAlert.confirm({
            title: `Delete "${s.display_name}"?`,
            message: `${s.email} will be removed from the From dropdown. Past sends keep their audit trail.`,
            confirmLabel: "Delete", danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/dnews/senders/${s.id}`, { method: "DELETE", credentials: "include" });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || "Delete failed");
            }
            dnAlert.success("Deleted");
            fetchAll();
        } catch (e) { dnAlert.error("Delete failed", e.message); }
    };

    return (
        <>
            <div className="dnews-el-toolbar">
                <div className="dnews-el-toolbar-info">
                    Senders shown here populate the <strong>From</strong> dropdown in Newsletter and Reminder.
                    Only addresses on <strong>{ALLOWED_SENDER_DOMAINS.join(" or ")}</strong> can be added.
                </div>
                <button
                    className="dnews-el-primary-btn"
                    onClick={() => { setEditing(null); setModalOpen(true); }}
                >
                    <FaPlus /> Add sender
                </button>
            </div>

            <div className="dnews-el-count">{senders.length} sender{senders.length === 1 ? "" : "s"}</div>

            {loading ? (
                <div className="dnews-el-empty">Loading…</div>
            ) : senders.length === 0 ? (
                <div className="dnews-el-empty">
                    No senders yet. The system will fall back to <code>connect@dolluzcorp.com</code>.
                    Click <strong>+ Add sender</strong> to manage the list.
                </div>
            ) : (
                <div className="dnews-el-table-wrap">
                    <table className="dnews-el-table">
                        <thead>
                            <tr>
                                <th>DISPLAY NAME</th>
                                <th>EMAIL</th>
                                <th>STATUS</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {senders.map(s => (
                                <tr key={s.id} className={!s.active ? "dnews-el-row-muted" : ""}>
                                    <td>{s.display_name}</td>
                                    <td>{s.email}</td>
                                    <td>
                                        <span className={`dnews-el-pill ${s.active ? "active" : "inactive"}`}>
                                            {s.active ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className="dnews-el-row-actions">
                                        <button
                                            className="dnews-el-icon-btn"
                                            title={s.active ? "Disable (hide from From dropdown)" : "Enable"}
                                            onClick={() => onToggle(s)}
                                        >
                                            {s.active ? <FaBan /> : <FaCheck />}
                                        </button>
                                        <button
                                            className="dnews-el-icon-btn"
                                            title="Edit"
                                            onClick={() => { setEditing(s); setModalOpen(true); }}
                                        >
                                            <FaPenSquare />
                                        </button>
                                        <button
                                            className="dnews-el-icon-btn danger"
                                            title="Delete"
                                            onClick={() => onDelete(s)}
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {modalOpen && (
                <SenderModal
                    initial={editing}
                    onClose={() => setModalOpen(false)}
                    onSaved={fetchAll}
                />
            )}
        </>
    );
}

function SenderModal({ initial, onClose, onSaved }) {
    const isEdit = !!initial;
    const [display_name, setDisplayName] = useState(initial?.display_name || "");
    const [email, setEmail] = useState(initial?.email || "");
    const [active, setActive] = useState(initial ? !!initial.active : true);
    const [busy, setBusy] = useState(false);

    const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email.trim().toLowerCase();
    const domain = trimmedEmail.split("@")[1] || "";
    const domainOk = ALLOWED_SENDER_DOMAINS.includes(domain);
    const emailOk = EMAIL_RX.test(trimmedEmail) && domainOk;

    const onSave = async () => {
        if (!display_name.trim()) { dnAlert.warning("Display name is required"); return; }
        if (!email.trim())        { dnAlert.warning("Email is required"); return; }
        if (!EMAIL_RX.test(trimmedEmail)) { dnAlert.warning("Invalid email", `"${email}" is not a valid email.`); return; }
        if (!domainOk) {
            dnAlert.warning(
                "Domain not allowed",
                `Sender domain must be ${ALLOWED_SENDER_DOMAINS.join(" or ")} (SendGrid-authenticated).`
            );
            return;
        }
        setBusy(true);
        try {
            const url = isEdit ? `/api/dnews/senders/${initial.id}` : "/api/dnews/senders/create";
            const res = await apiFetch(url, {
                method: isEdit ? "PUT" : "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ display_name, email: trimmedEmail, active }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");
            dnAlert.success(isEdit ? "Updated" : "Added");
            onSaved(); onClose();
        } catch (e) {
            dnAlert.error("Save failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="dnews-el-modal-backdrop">
            <div className="dnews-el-modal" onClick={e => e.stopPropagation()}>
                <div className="dnews-el-modal-header">
                    <h3>{isEdit ? "Edit Sender" : "Add Sender"}</h3>
                    <button onClick={onClose}><FaTimes /></button>
                </div>
                <div className="dnews-el-modal-body">
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Display name</label>
                        <input
                            className="dnews-input"
                            value={display_name}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="e.g. Dolluz HR"
                            maxLength={120}
                        />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Email</label>
                        <input
                            className={`dnews-input ${email && !emailOk ? "dnews-input-error" : ""}`}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={`name@${ALLOWED_SENDER_DOMAINS[0]}`}
                            maxLength={255}
                        />
                        {email && !emailOk && (
                            <div className="dnews-input-err-list">
                                {!EMAIL_RX.test(trimmedEmail)
                                    ? "Invalid email format."
                                    : `Domain must be ${ALLOWED_SENDER_DOMAINS.join(" or ")}.`}
                            </div>
                        )}
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-checkbox-row">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                            <span>Active (show in From dropdown)</span>
                        </label>
                    </div>
                </div>
                <div className="dnews-el-modal-footer">
                    <button className="dnews-el-secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="dnews-submit-btn" onClick={onSave} disabled={busy}>
                        {busy
                            ? <><span className="dn-spinner" /> Saving…</>
                            : (isEdit ? "Save changes" : "Add sender")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// GROUPS TAB
// ═══════════════════════════════════════════════════════════════════════════
function GroupsTab({ navigate }) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);    // group id being edited

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dnews/groups/list", { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            setGroups(await res.json());
        } finally { setLoading(false); }
    }, [navigate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const onCreate = async () => {
        const value = await dnAlert.prompt({
            title: "New distribution list",
            placeholder: "Group name (e.g. Healthcare Clients)",
            confirmLabel: "Create",
            validate: (v) => !v || !v.trim() ? "Name is required" : null,
        });
        if (!value) return;
        const res = await apiFetch("/api/dnews/groups/create", {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: value.trim() }),
        });
        const data = await res.json();
        if (!res.ok) { dnAlert.error("Failed", data.error); return; }
        await fetchList();
        setEditing(data.id);
    };

    const onDelete = async (g) => {
        const ok = await dnAlert.confirm({
            title: `Delete "${g.name}"?`,
            message: `This group and its ${g.member_count} member${g.member_count === 1 ? "" : "s"} will be removed.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await apiFetch(`/api/dnews/groups/${g.id}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) { dnAlert.error("Failed"); return; }
        await fetchList();
    };

    if (editing) {
        return <GroupEditor groupId={editing} onBack={() => { setEditing(null); fetchList(); }} navigate={navigate} />;
    }

    return (
        <>
            <div className="dnews-el-toolbar">
                <div className="dnews-el-count" style={{ flex: 1 }}>
                    {loading ? "Loading..." : `${groups.length} group${groups.length === 1 ? "" : "s"}`}
                </div>
                <button className="dnews-el-primary-btn" onClick={onCreate}>
                    <FaPlus /> New group
                </button>
            </div>

            <div className="dnews-el-group-grid">
                {!loading && groups.length === 0 && (
                    <div className="dnews-el-empty" style={{ gridColumn: "1/-1", padding: 40 }}>
                        No groups yet. Click <strong>+ New group</strong> to create one.
                    </div>
                )}
                {groups.map(g => (
                    <div key={g.id} className="dnews-el-group-card">
                        <div className="dnews-el-group-name">
                            <FaFolderOpen /> {g.name}
                        </div>
                        {g.description && <div className="dnews-el-group-desc">{g.description}</div>}
                        <div className="dnews-el-group-meta">
                            <span className="dnews-el-type-pill client">{g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                        </div>
                        <div className="dnews-el-group-actions">
                            <button className="dnews-el-secondary-btn" onClick={() => setEditing(g.id)}>
                                <FaPenSquare /> Edit members
                            </button>
                            <button className="dnews-el-icon-btn danger" onClick={() => onDelete(g)}>
                                <FaTrashAlt />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

function GroupEditor({ groupId, onBack, navigate }) {
    const [group, setGroup] = useState(null);
    const [audience, setAudience] = useState(null);
    const [activeTab, setActiveTab] = useState("employee");
    const [empSel, setEmpSel] = useState([]);
    const [contactSel, setContactSel] = useState([]);
    const [rawText, setRawText] = useState("");
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchGroup = useCallback(async () => {
        try {
            const [gRes, audRes] = await Promise.all([
                apiFetch(`/api/dnews/groups/${groupId}`, { credentials: "include" }),
                apiFetch("/api/dnews/newsletter/audience-options", { credentials: "include" }),
            ]);
            if (gRes.status === 401 || gRes.status === 403) { navigate("/login"); return; }
            const g = await gRes.json();
            setGroup(g);
            setName(g.name || ""); setDesc(g.description || "");
            setAudience(await audRes.json());
        } catch { /* tolerated */ }
    }, [groupId, navigate]);

    useEffect(() => { fetchGroup(); }, [fetchGroup]);

    // Live-parse the raw textarea so the UI can red-flag bad emails before
    // the user clicks Add. Memoised by `rawText` so re-renders are cheap.
    const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rawTokens = rawText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    const invalidRaw = rawTokens.filter(e => !EMAIL_RX.test(e));
    const validRaw   = rawTokens.filter(e =>  EMAIL_RX.test(e));

    const onAddMembers = async () => {
        // Strict all-or-nothing: any bad email blocks the entire add, even if
        // the employee + contact selections are perfectly valid. User must
        // fix or remove the bad emails first. This matches the "everything
        // alright or nothing saves" requirement.
        if (invalidRaw.length > 0) {
            dnAlert.error(
                `${invalidRaw.length} invalid email${invalidRaw.length === 1 ? "" : "s"}`,
                `Fix or remove: ${invalidRaw.slice(0, 3).join(", ")}${invalidRaw.length > 3 ? "…" : ""}`
            );
            return;
        }

        // Combine selections from ALL three tabs - not just the currently-open
        // one. The tab is just a UI grouping; the user's intent is "add
        // everything I've picked".
        const members = [
            ...empSel.map(s     => ({ member_type: "employee", id_or_email: String(s.value) })),
            ...contactSel.map(s => ({ member_type: "contact",  id_or_email: String(s.value) })),
            ...validRaw.map(e   => ({ member_type: "raw",      id_or_email: e })),
        ];

        if (members.length === 0) {
            dnAlert.warning("Pick at least one member"); return;
        }

        setBusy(true);
        try {
            const res = await apiFetch(`/api/dnews/groups/${groupId}/members`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ members }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            // All invalid emails were rejected above, so server-side skips
            // here can only be duplicates.
            const dupMsg = data.skipped > 0
                ? `${data.skipped} already in this group, skipped`
                : undefined;

            if (data.added > 0)         dnAlert.success(`Added ${data.added}`, dupMsg);
            else if (data.skipped > 0)  dnAlert.info("Already in group", dupMsg);
            else                         dnAlert.warning("Nothing to add");

            setEmpSel([]); setContactSel([]); setRawText("");
            await fetchGroup();
        } catch (e) {
            dnAlert.error("Failed", e.message);
        } finally { setBusy(false); }
    };

    const onRemoveMember = async (memberId) => {
        const res = await apiFetch(`/api/dnews/groups/${groupId}/members/${memberId}`, {
            method: "DELETE", credentials: "include",
        });
        if (!res.ok) { dnAlert.error("Failed"); return; }
        await fetchGroup();
    };

    const onRename = async () => {
        if (!name.trim()) { dnAlert.warning("Name is required"); return; }
        const res = await apiFetch(`/api/dnews/groups/${groupId}`, {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name, description: desc }),
        });
        const data = await res.json();
        if (!res.ok) { dnAlert.error("Failed", data.error); return; }
        dnAlert.success("Saved");
        await fetchGroup();
    };

    if (!group) return <div className="dnews-el-empty" style={{ padding: 40 }}>Loading group…</div>;

    return (
        <>
            <div className="dnews-el-toolbar">
                <button className="dnews-el-secondary-btn" onClick={onBack}>← Back to groups</button>
                <div style={{ flex: 1 }} />
            </div>

            <section className="dnews-card">
                <div className="dnews-row">
                    <label className="dnews-label dnews-label-req">Group name</label>
                    <input className="dnews-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
                </div>
                <div className="dnews-row">
                    <label className="dnews-label">Description (optional)</label>
                    <input className="dnews-input" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={250} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button className="dnews-el-secondary-btn" onClick={onRename}>Save group info</button>
                </div>
            </section>

            <section className="dnews-card" style={{ marginTop: 14 }}>
                <h3 className="dnews-el-section-h">Members ({group.members.length})</h3>
                {group.members.length === 0 && (
                    <p className="dnews-el-empty" style={{ padding: "10px 0" }}>No members yet. Use the picker below to add some.</p>
                )}
                <div className="dnews-el-member-chips">
                    {group.members.map(m => (
                        <span key={m.id} className={`dnews-el-member-chip kind-${m.member_type}`}>
                            <strong>{m.label || "-"}</strong>
                            <span className="dnews-el-member-sub">{m.email || ""}</span>
                            <span className="dnews-el-member-meta">{m.sub}</span>
                            <button onClick={() => onRemoveMember(m.id)} title="Remove"><FaTimes /></button>
                        </span>
                    ))}
                </div>

                <div className="dnews-el-add-tabs">
                    {[
                        { id: "employee", label: "Add employee" },
                        { id: "contact",  label: "Add contact" },
                        { id: "raw",      label: "Paste emails" },
                    ].map(t => (
                        <button
                            key={t.id}
                            className={`dnews-el-add-tab ${activeTab === t.id ? "active" : ""}`}
                            onClick={() => setActiveTab(t.id)}
                        >{t.label}</button>
                    ))}
                </div>

                {activeTab === "employee" && (
                    <Select
                        isMulti
                        {...checkboxMultiProps}
                        options={audience?.employees || []}
                        value={empSel}
                        onChange={(s) => setEmpSel(s || [])}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Search employees..."
                    />
                )}
                {activeTab === "contact" && (
                    <Select
                        isMulti
                        {...checkboxMultiProps}
                        options={audience?.contacts || []}
                        value={contactSel}
                        onChange={(s) => setContactSel(s || [])}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Search contacts..."
                    />
                )}
                {activeTab === "raw" && (
                    <>
                        <textarea
                            className={`dnews-input ${invalidRaw.length > 0 ? "dnews-input-error" : ""}`}
                            rows={4}
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                            placeholder="alice@acme.com, bob@example.com"
                        />
                        {/* Inline validation feedback - red when any bad emails
                            present, green count when everything parses clean. */}
                        {rawTokens.length > 0 && (
                            invalidRaw.length > 0 ? (
                                <div className="dnews-input-err-list">
                                    <strong>{invalidRaw.length} invalid email{invalidRaw.length === 1 ? "" : "s"}:</strong>
                                    {" "}
                                    {invalidRaw.slice(0, 5).map((e, i) => (
                                        <span key={i} className="dnews-input-err-chip">{e}</span>
                                    ))}
                                    {invalidRaw.length > 5 && <span> +{invalidRaw.length - 5} more</span>}
                                </div>
                            ) : (
                                <div className="dnews-input-ok-hint">
                                    ✓ {validRaw.length} valid email{validRaw.length === 1 ? "" : "s"} ready to add
                                </div>
                            )
                        )}
                    </>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <button
                        className="dnews-submit-btn"
                        onClick={onAddMembers}
                        disabled={busy || invalidRaw.length > 0}
                    >
                        {busy ? <><span className="dn-spinner" /> Adding…</> : "Add to group"}
                    </button>
                </div>
            </section>
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// IMPORT TAB
// ═══════════════════════════════════════════════════════════════════════════
function ImportTab() {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);       // [{row, field, message}, ...]
    const [totalErrors, setTotalErrors] = useState(0);

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setErrors([]);
        setTotalErrors(0);
    };

    // Backend streams a styled XLSX (navy header, orange Type accent, zebra
    // rows, frozen header) - matches dSpr Reports export theme. Fetch + blob
    // because the endpoint is JWT-protected.
    const downloadSampleTemplate = async () => {
        try {
            const res = await apiFetch("/api/dnews/import/sample-template", { credentials: "include" });
            if (!res.ok) throw new Error("Server refused the download");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "dnews_contacts_sample.xlsx";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            dnAlert.error("Couldn't download template", e.message);
        }
    };

    // One-shot strict import. Server validates EVERY row before any insert;
    // any error → nothing imported, response carries per-row error list.
    const onImport = async () => {
        if (!file) { dnAlert.warning("Pick a file first"); return; }
        setBusy(true);
        setErrors([]);
        setTotalErrors(0);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await apiFetch("/api/dnews/import/commit", { method: "POST", credentials: "include", body: fd });
            const data = await res.json();

            if (!res.ok) {
                // Validation failure - backend returns { errors: [...], total_errors }
                if (Array.isArray(data.errors) && data.errors.length > 0) {
                    setErrors(data.errors);
                    setTotalErrors(data.total_errors || data.errors.length);
                    dnAlert.error(
                        `${data.total_errors || data.errors.length} validation error${(data.total_errors || data.errors.length) === 1 ? "" : "s"}`,
                        "Nothing was imported. Fix the rows below and re-upload."
                    );
                } else {
                    dnAlert.error("Import failed", data.error || "Unknown error");
                }
                return;
            }

            const dupMsg = data.skipped_dup > 0 ? `${data.skipped_dup} duplicates already existed and were skipped.` : undefined;
            dnAlert.success(`Imported ${data.inserted} contact${data.inserted === 1 ? "" : "s"}`, dupMsg);
            setFile(null);
        } catch (e) {
            dnAlert.error("Import failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <section className="dnews-card">
            <div className="dnews-el-import-header">
                <div>
                    <h3 className="dnews-el-section-h" style={{ marginBottom: 4 }}>Bulk import from CSV / XLSX</h3>
                    <p className="dnews-el-import-hint" style={{ margin: 0 }}>
                        Download the sample template, fill your data, upload. If any row has an error the whole file is rejected - nothing is imported until everything is valid.
                    </p>
                </div>
                <button
                    type="button"
                    className="dnews-el-sample-btn"
                    onClick={downloadSampleTemplate}
                    disabled={busy}
                    title="Download the sample XLSX with required headers + Type dropdown"
                >
                    <FaDownload /> Sample template
                </button>
            </div>

            {/* Wrap the upload zone so we can lock it while an import is
                in flight - prevents the admin swapping in a different file
                halfway through and creating an ambiguous request. */}
            <div className={`dnews-el-upload-zone ${busy ? "is-busy" : ""}`}>
                <FaUpload />
                <div>
                    <p>{file ? file.name : "Click to upload a CSV or XLSX file"}</p>
                    <p className="dnews-el-upload-hint">
                        Up to 5 MB · 500+ rows supported · Required columns: <strong>Name</strong>, <strong>Email</strong>
                    </p>
                </div>
                <input type="file" accept=".csv,.xlsx" onChange={onPickFile} disabled={busy} />
            </div>

            {/* Errors block - shown only after a failed import. Lists each
                offending row by spreadsheet row number so admins can fix in
                Excel and re-upload. Capped to 50 in the response for size. */}
            {errors.length > 0 && (
                <div className="dnews-el-err-panel">
                    <div className="dnews-el-err-head">
                        <FaTimes /> {totalErrors} error{totalErrors === 1 ? "" : "s"} - nothing was imported
                    </div>
                    <ul className="dnews-el-err-rows">
                        {errors.map((err, i) => (
                            <li key={i}>
                                <span className="dnews-el-err-row-num">Row {err.row}</span>
                                <span className="dnews-el-err-row-field">{err.field}</span>
                                <span className="dnews-el-err-row-msg">{err.message}</span>
                            </li>
                        ))}
                    </ul>
                    {totalErrors > errors.length && (
                        <div className="dnews-el-err-foot">
                            … and {totalErrors - errors.length} more. Fix what's shown, re-upload, and we'll show the next batch.
                        </div>
                    )}
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button className="dnews-submit-btn" onClick={onImport} disabled={busy || !file}>
                    {busy
                        ? <><span className="dn-spinner" /> Importing...</>
                        : <><FaArrowRight /> Import contacts</>}
                </button>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// UNSUBSCRIBES TAB - the record of people who clicked "Unsubscribe" in a
// dNews email. They are excluded from every future send automatically; an
// admin can resubscribe someone here if they ask to be added back.
// ═══════════════════════════════════════════════════════════════════════════
function UnsubscribesTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dnews/unsubscribe/list", { credentials: "include" });
            setRows(await res.json());
        } catch { setRows([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const resubscribe = async (r) => {
        const ok = await dnAlert.confirm({
            title: `Resubscribe ${r.email}?`,
            message: "They will start receiving dNews newsletters and reminders again.",
            confirmLabel: "Resubscribe",
        });
        if (!ok) return;
        try {
            const res = await apiFetch("/api/dnews/unsubscribe/manual-resubscribe", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: r.email, label_id: r.label_id }),
            });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || "Failed"); }
            dnAlert.success("Resubscribed");
            fetchAll();
        } catch (e) { dnAlert.error("Failed", e.message); }
    };

    const needle = q.trim().toLowerCase();
    const visible = rows.filter((r) =>
        !needle || `${r.email} ${r.name || ""}`.toLowerCase().includes(needle));
    const activeCount = rows.filter((r) => !r.resubscribed_at).length;
    const fmt = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "-");

    return (
        <>
            <div className="dnews-el-toolbar">
                <div className="dnews-el-toolbar-info">
                    People who unsubscribed from a specific dNews <strong>label</strong>. They're skipped
                    only for that label's newsletters and reminders - other labels and transactional mail
                    (OTP, password reset) are unaffected. Resubscribe undoes it for that label.
                </div>
                <div className="dnews-el-search">
                    <FaSearch />
                    <input type="text" placeholder="Search email or name…"
                        value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
            </div>

            <div className="dnews-el-count">
                {activeCount} unsubscribed{rows.length !== activeCount ? ` · ${rows.length - activeCount} later resubscribed` : ""}
            </div>

            {loading ? (
                <div className="dnews-el-empty">Loading…</div>
            ) : visible.length === 0 ? (
                <div className="dnews-el-empty">
                    Nobody has unsubscribed yet. When someone clicks the Unsubscribe link in an email, they show up here.
                </div>
            ) : (
                <div className="dnews-el-table-wrap">
                    <table className="dnews-el-table">
                        <thead>
                            <tr>
                                <th>EMAIL</th>
                                <th>NAME</th>
                                <th>LABEL</th>
                                <th>STATUS</th>
                                <th>UNSUBSCRIBED</th>
                                <th>FROM EMAIL</th>
                                <th>SOURCE</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((r) => {
                                const active = !r.resubscribed_at;
                                return (
                                    <tr key={r.id}>
                                        <td>{r.email}</td>
                                        <td>{r.name || "-"}</td>
                                        <td>{r.label_name || (r.label_id ? `#${r.label_id}` : "-")}</td>
                                        <td>
                                            <span className={`dnews-el-pill ${active ? "unsub" : "active"}`}>
                                                {active ? "Unsubscribed" : "Resubscribed"}
                                            </span>
                                        </td>
                                        <td>{fmt(r.unsubscribed_at)}</td>
                                        <td>{r.job_subject || "-"}</td>
                                        <td>{r.source === "manual" ? "Admin" : r.source === "one-click" ? "One-click" : "Link"}</td>
                                        <td>
                                            {active ? (
                                                <button className="dnews-el-icon-btn"
                                                    title="Undo this unsubscribe - they will receive dNews emails again"
                                                    style={{ width: "auto", padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}
                                                    onClick={() => resubscribe(r)}>
                                                    <FaCheck /> Resubscribe
                                                </button>
                                            ) : (
                                                <span style={{ color: "#94a3b8" }}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
